import { useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type {
  DashboardStats,
  QueueHealth,
  SyncStatusResponse,
} from '@/features/chronograph-dashboard/types'
import {
  formatDateTime,
  formatRelativeTime,
  formatSyncTypeLabel,
  MetricPair,
  OverviewCard,
} from '@/components/custom/chronograph/shared'

interface OverviewTabProps {
  dashboardData: DashboardStats | undefined
  syncStatusData: SyncStatusResponse | undefined
  todayOutcome: { created: number; updated: number }
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function OverviewTab({
  dashboardData,
  syncStatusData,
  todayOutcome,
  isLoading,
  isError,
  onRetry,
}: OverviewTabProps) {
  const totalFailedRuns =
    dashboardData?.byType.reduce((sum, type) => sum + type.failedRuns, 0) ?? 0
  const failedSummary =
    totalFailedRuns > 0
      ? dashboardData?.lastFailedSync
        ? `Last failure ${formatRelativeTime(dashboardData.lastFailedSync)}`
        : 'Recent failures detected'
      : 'No failures in last 30 days'
  const queueHealth = syncStatusData?.queue

  // Staleness: warn if last sync > 24h, danger if > 48h
  const lastSyncDate =
    syncStatusData?.lastSync?.completedAt ?? dashboardData?.lastSuccessfulSync
  const lastSyncTone = useMemo(() => {
    if (!syncStatusData?.isEnabled) return 'warning' as const
    if (!lastSyncDate) return 'warning' as const
    const now = new Date()
    const hoursAgo =
      (now.getTime() - new Date(lastSyncDate).getTime()) / (1000 * 60 * 60)
    if (hoursAgo > 48) return 'danger' as const
    if (hoursAgo > 24) return 'warning' as const
    return 'neutral' as const
  }, [syncStatusData?.isEnabled, lastSyncDate])

  return (
    <div className="space-y-5">
      {/* Stat Cards */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {isError ? (
          <Card className="border-destructive/20 bg-background shadow-none md:col-span-2 xl:col-span-5">
            <CardContent className="px-4 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <Typography
                    variant="small"
                    className="font-medium text-destructive"
                  >
                    Unable to load overview
                  </Typography>
                  <Typography variant="body-sm" className="text-primary/70">
                    There was a problem loading the latest Chronograph stats.
                  </Typography>
                </div>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <Card
              key={index}
              className="border-primary/12 bg-background shadow-none"
            >
              <CardContent className="space-y-2 px-4 py-3.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-28" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <OverviewCard
              label="Companies & Investments"
              value={(
                (syncStatusData?.syncedRecords.companies ?? 0) +
                (syncStatusData?.syncedRecords.investments ?? 0)
              ).toLocaleString()}
              meta={`${(syncStatusData?.syncedRecords.companies ?? 0).toLocaleString()} companies \u2022 ${(syncStatusData?.syncedRecords.investments ?? 0).toLocaleString()} investments`}
            />
            <OverviewCard
              label="Metric Data Points"
              value={(
                syncStatusData?.syncedRecords.financialMetrics ?? 0
              ).toLocaleString()}
              meta="Imported financial metric rows"
            />
            <OverviewCard
              label="Last Incremental Sync"
              value={formatRelativeTime(lastSyncDate)}
              meta={
                syncStatusData?.lastSync
                  ? `${formatSyncTypeLabel(syncStatusData.lastSync.type)} \u2022 ${formatDateTime(syncStatusData.lastSync.completedAt)}`
                  : `${syncStatusData?.dataSource ?? 'Chronograph'} \u2022 ${formatDateTime(dashboardData?.lastSuccessfulSync)}`
              }
              tone={lastSyncTone}
            />
            <OverviewCard
              label="Failed Runs"
              value={String(totalFailedRuns)}
              meta={failedSummary}
              icon={<AlertTriangle className="size-3.5" />}
              tone={totalFailedRuns > 0 ? 'danger' : 'neutral'}
              emphasize={totalFailedRuns > 0}
            />
            <OverviewCard
              label="Today's Activity"
              value={`${(todayOutcome.created + todayOutcome.updated).toLocaleString()} records`}
              meta={`${todayOutcome.created.toLocaleString()} created \u2022 ${todayOutcome.updated.toLocaleString()} updated`}
              tone={
                todayOutcome.created > 0 || todayOutcome.updated > 0
                  ? 'positive'
                  : 'neutral'
              }
            />
          </>
        )}
      </div>

      {/* Compact Queue Health Strip */}
      {queueHealth ? <CompactQueueStrip queueHealth={queueHealth} /> : null}

      {/* Sync Performance by Type */}
      <SyncPerformanceByType
        byType={dashboardData?.byType ?? []}
        isLoading={isLoading}
      />
    </div>
  )
}

function CompactQueueStrip({ queueHealth }: { queueHealth: QueueHealth }) {
  const states = [
    'waiting',
    'active',
    'completed',
    'failed',
    'delayed',
  ] as const

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-primary/12 bg-primary/[0.015] px-4 py-3">
      <Typography variant="body-sm" className="font-medium text-primary/70">
        Queue
      </Typography>
      {states.map((state) => (
        <div key={state} className="flex items-center gap-1.5">
          <Typography variant="body-sm" className="capitalize text-primary/70">
            {state}:
          </Typography>
          <Typography
            variant="body-sm"
            className={cn(
              'font-inter font-medium tabular-nums',
              state === 'failed' &&
                queueHealth[state] > 0 &&
                'text-destructive',
              state === 'active' && queueHealth[state] > 0 && 'text-green-600'
            )}
          >
            {queueHealth[state]}
          </Typography>
        </div>
      ))}
    </div>
  )
}

function SyncPerformanceByType({
  byType,
  isLoading,
}: {
  byType: DashboardStats['byType']
  isLoading: boolean
}) {
  return (
    <Card className="border-primary/18 bg-primary/[0.015] shadow-none">
      <div className="px-4 pb-3 pt-4 lg:px-5">
        <Typography
          variant="section-title"
          as="span"
          className="font-semibold text-primary"
        >
          Sync Performance by Type
        </Typography>
        <Typography variant="body-sm" className="mt-1 text-primary/70">
          Per-type volume, throughput, and success rate for the recent dashboard
          window.
        </Typography>
      </div>
      <div className="space-y-3 px-4 pb-4 lg:px-5">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          byType.map((stat) => (
            <div
              key={stat.type}
              className="flex flex-col gap-4 rounded-lg border border-primary/12 bg-background px-4 py-3.5 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="min-w-0 flex-1">
                <Typography variant="small">
                  {formatSyncTypeLabel(stat.type)}
                </Typography>
                <Typography variant="body-sm" className="mt-1 text-primary/70">
                  {stat.totalRuns} runs &middot; {stat.avgDuration ?? 'N/A'} avg
                </Typography>
              </div>
              <div className="grid grid-cols-4 gap-4 text-right">
                <MetricPair
                  label="Created"
                  value={stat.totalCreated.toLocaleString()}
                />
                <MetricPair
                  label="Updated"
                  value={stat.totalUpdated.toLocaleString()}
                />
                <MetricPair
                  label="Skipped"
                  value={stat.totalSkipped.toLocaleString()}
                />
                <div className="flex items-end justify-end">
                  <Badge
                    variant={stat.successRate === 100 ? 'green' : 'outline'}
                    className="font-inter tabular-nums"
                  >
                    {stat.successRate.toFixed(0)}%
                  </Badge>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  )
}
